import React, { useState } from "react";

// ── Nav ──────────────────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { id: "auth",        label: "Authentication",      color: "#5F5E5A", section: "Overview" },
  { id: "get_operator",label: "Fetch Recharge Operator", color: "#185FA5", section: "Recharge API" },
  { id: "dth_operator",label: "Fetch DTH Operator",   color: "#185FA5", section: null },
  { id: "get_circle",  label: "Fetch Recharge Circle",color: "#185FA5", section: null },
  { id: "create_mobile", label: "Create Mobile Recharge", color: "#3B6D11", section: null },
  { id: "create_dth",  label: "Create DTH Recharge",  color: "#3B6D11", section: null },
  { id: "recharge_status", label: "Recharge Status Check", color: "#BA7517", section: null },
  { id: "recharge_callback", label: "Recharge Callback", color: "#D85A30", section: null },
  { id: "bbps_operator", label: "Fetch BBPS Operator", color: "#185FA5", section: "BBPS" },
  { id: "bbps_status", label: "BBPS Status Check",     color: "#BA7517", section: null },
  { id: "bbps_bill",   label: "Fetch Biller Info",     color: "#534AB7", section: null },
  { id: "bbps_pay",    label: "Pay BBPS Bill Online",  color: "#3B6D11", section: null },
  { id: "bbps_pay_manual", label: "Pay BBPS Bill Offline", color: "#3B6D11", section: null },
  { id: "balance",     label: "Fetch API Balance",     color: "#534AB7", section: "Account" },
];

// ── Shared styles ───────────────────────────────────────────────────────────
const styles = {
  methodBadge: (method) => ({
    display: "inline-block", padding: "3px 9px", borderRadius: 6,
    fontSize: 11, fontWeight: 600, fontFamily: "monospace",
    background: method === "POST" ? "#EAF3DE" : "#E6F1FB",
    color: method === "POST" ? "#3B6D11" : "#0C447C",
  }),
  badge: (type) => ({
    display: "inline-block", padding: "1px 7px", borderRadius: 4,
    fontSize: 10, fontWeight: 600,
    background: type === "required" ? "#FCEBEB" : "#F1EFE8",
    color: type === "required" ? "#A32D2D" : "#5F5E5A",
  }),
  codeBlock: {
    background: "#F8FAFC", border: "0.5px solid #e2e8f0", borderRadius: 8,
    padding: "14px 16px", fontFamily: "monospace", fontSize: 12,
    lineHeight: 1.8, overflowX: "auto", position: "relative", whiteSpace: "pre",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
  th: {
    padding: "8px 10px", textAlign: "left", background: "#f8fafc",
    color: "#64748b", fontWeight: 600, fontSize: 11, borderBottom: "0.5px solid #e2e8f0",
  },
  td: { padding: "9px 10px", borderBottom: "0.5px solid #f1f5f9", verticalAlign: "top" },
  noteBox: (color = "#BA7517") => ({
    background: color === "#BA7517" ? "#FAEEDA" : color === "#185FA5" ? "#E6F1FB" : "#FCEBEB",
    borderLeft: `3px solid ${color}`, padding: "10px 14px", fontSize: 12,
    color: color === "#BA7517" ? "#633806" : color === "#185FA5" ? "#0C447C" : "#A32D2D",
    marginBottom: 16,
  }),
};

function CopyButton({ getText }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(getText()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button onClick={copy} style={{
      position: "absolute", top: 8, right: 8, padding: "3px 10px", background: "#fff",
      border: "0.5px solid #e2e8f0", borderRadius: 6, fontSize: 11, cursor: "pointer", color: "#64748b",
    }}>
      {copied ? "Copied!" : "Copy"}
    </button>
  );
}

function CodeBlock({ code }) {
  return (
    <div style={styles.codeBlock}>
      <CopyButton getText={() => code} />
      <div>{code}</div>
    </div>
  );
}

function SectionLabel({ children, style = {} }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 600, color: "#64748b", letterSpacing: ".06em",
      textTransform: "uppercase", marginBottom: 10, marginTop: 20, ...style,
    }}>
      {children}
    </div>
  );
}

function BaseUrl({ method, path, query }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 10, background: "#f8fafc",
      border: "0.5px solid #e2e8f0", borderRadius: 8, padding: "8px 14px",
      fontFamily: "monospace", fontSize: 12, marginTop: 10,
    }}>
      <span style={styles.methodBadge(method)}>{method}</span>
      <span style={{ color: "#64748b" }}>https://pro.mokshiq.in/</span>
      <span style={{ color: "#185FA5", fontWeight: 600 }}>{path}</span>
      {query && <span style={{ color: "#94a3b8" }}>{query}</span>}
    </div>
  );
}

function ParamRow({ name, type, required, desc }) {
  return (
    <tr>
      <td style={styles.td}><span style={{ fontFamily: "monospace", fontSize: 12, color: "#185FA5" }}>{name}</span></td>
      <td style={styles.td}><span style={{ color: "#64748b" }}>{type}</span></td>
      <td style={styles.td}>
        <span style={styles.badge(required ? "required" : "optional")}>
          {required ? "required" : "optional"}
        </span>
      </td>
      <td style={{ ...styles.td, color: "#374151" }}>{desc}</td>
    </tr>
  );
}

function ParamTable({ rows }) {
  return (
    <div style={{ border: "0.5px solid #e2e8f0", borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Parameter</th>
            <th style={styles.th}>Type</th>
            <th style={styles.th}>Required</th>
            <th style={styles.th}>Description</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => <ParamRow key={i} {...r} />)}
        </tbody>
      </table>
    </div>
  );
}

// Every real endpoint requires these two headers
const HEADERS_NOTE = (
  <div style={styles.noteBox("#185FA5")}>
    Every request must include <code style={{ fontFamily: "monospace" }}>Authorization: Bearer &lt;token&gt;</code>{" "}
    and <code style={{ fontFamily: "monospace" }}>Origin: &lt;your registered client origin&gt;</code>.
  </div>
);

function EndpointHeader({ title, desc, method, path, query }) {
  return (
    <div style={{ marginBottom: 24, paddingBottom: 16, borderBottom: "0.5px solid #e2e8f0" }}>
      <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 4, color: "#0f172a" }}>{title}</h2>
      <p style={{ fontSize: 13, color: "#64748b" }}>{desc}</p>
      <BaseUrl method={method} path={path} query={query} />
    </div>
  );
}

// ── Pages ─────────────────────────────────────────────────────────────────────

function AuthPage() {
  return (
    <div>
      <div style={{ marginBottom: 24, paddingBottom: 16, borderBottom: "0.5px solid #e2e8f0" }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 4, color: "#0f172a" }}>Authentication</h2>
        <p style={{ fontSize: 13, color: "#64748b" }}>
          All requests must include your Bearer token and the Origin header used when the token was issued.
        </p>
      </div>
      <SectionLabel style={{ marginTop: 0 }}>Request headers</SectionLabel>
      <div style={{ border: "0.5px solid #e2e8f0", borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
        <table style={styles.table}>
          <thead><tr><th style={styles.th}>Header</th><th style={styles.th}>Value</th><th style={styles.th}>Description</th></tr></thead>
          <tbody>
            <tr>
              <td style={styles.td}><span style={{ fontFamily: "monospace", fontSize: 12, color: "#185FA5" }}>Authorization</span></td>
              <td style={styles.td}><span style={{ fontFamily: "monospace", fontSize: 12, color: "#3B6D11" }}>Bearer &lt;token&gt;</span></td>
              <td style={styles.td}>Your JWT / API key</td>
            </tr>
            <tr>
              <td style={{ ...styles.td, borderBottom: "none" }}><span style={{ fontFamily: "monospace", fontSize: 12, color: "#185FA5" }}>Origin</span></td>
              <td style={{ ...styles.td, borderBottom: "none" }}><span style={{ fontFamily: "monospace", fontSize: 12, color: "#3B6D11" }}>API Partner Dashboard Url</span></td>
              <td style={{ ...styles.td, borderBottom: "none" }}>Must match the client_id embedded in your token</td>
            </tr>
          </tbody>
        </table>
      </div>
      <SectionLabel>Example (curl)</SectionLabel>
      <CodeBlock code={`curl --location 'https://pro.mokshiq.in/get_operator' \\
--header 'Authorization: Bearer <token>' \\
--header 'Origin: <origin>'`} />
      <div style={{ ...styles.noteBox("#D85A30"), marginTop: 16 }}>
        Keep your token secret. Never expose it in frontend code or public repositories.
      </div>
    </div>
  );
}

function GetOperatorPage() {
  return (
    <div>
      <EndpointHeader
        title="Fetch Recharge Operator"
        desc="Returns available mobile/DTH recharge operators."
        method="GET" path="get_operator"
      />
      {HEADERS_NOTE}
      <SectionLabel style={{ marginTop: 0 }}>curl</SectionLabel>
      <CodeBlock code={`curl --location 'https://pro.mokshiq.in/get_operator' \\
--header 'Authorization: {{Authorization}}' \\
--header 'Origin: {{Origin}}'`} />
    </div>
  );
}

function DthOperatorPage() {
  return (
    <div>
      <EndpointHeader
        title="Fetch DTH Operator"
        desc="Returns available DTH operators for a given type."
        method="GET" path="fetch_dth_operator" query="?type=dth"
      />
      {HEADERS_NOTE}
      <SectionLabel style={{ marginTop: 0 }}>Query parameters</SectionLabel>
      <ParamTable rows={[{ name: "type", type: "string", required: true, desc: 'Operator category, e.g. "dth"' }]} />
      <SectionLabel>curl</SectionLabel>
      <CodeBlock code={`curl --location 'https://pro.mokshiq.in/fetch_dth_operator?type=dth' \\
--header 'Authorization: {{Authorization}}' \\
--header 'Origin: {{Origin}}'`} />
    </div>
  );
}

function CircleePage() {
  return (
    <div>
      <EndpointHeader
        title="Fetch Recharge Circle"
        desc="Returns the list of telecom circles used for prepaid recharge."
        method="GET" path="get_circle"
      />
      {HEADERS_NOTE}
      <SectionLabel style={{ marginTop: 0 }}>curl</SectionLabel>
      <CodeBlock code={`curl --location 'https://pro.mokshiq.in/get_circle' \\
--header 'Authorization: {{Authorization}}' \\
--header 'Origin: {{Origin}}'`} />
    </div>
  );
}

function CreateMobileRechargePage() {
  return (
    <div>
      <EndpointHeader
        title="Create Mobile Recharge"
        desc="Initiate a mobile prepaid recharge (multipart/form-data)."
        method="POST" path="create_mobile_recharge"
      />
      {HEADERS_NOTE}
      <SectionLabel style={{ marginTop: 0 }}>Form-data parameters</SectionLabel>
      <ParamTable rows={[
        { name: "operator", type: "string", required: true, desc: 'Operator name, e.g. "Vodafone", "Airtel", "Jio"' },
        { name: "number",   type: "string", required: true, desc: "10-digit mobile number" },
        { name: "amount",   type: "string", required: true, desc: "Recharge amount in INR" },
        { name: "pin",      type: "string", required: true, desc: "Transaction PIN" },
        { name: "circle",   type: "string", required: true, desc: 'Circle name, e.g. "Delhi NCR"' },
      ]} />
      <SectionLabel>curl</SectionLabel>
      <CodeBlock code={`curl --location 'https://pro.mokshiq.in/create_mobile_recharge' \\
--header 'Authorization: {{Authorization}}' \\
--header 'Origin: {{Origin}}' \\
--form 'operator="Vodafone"' \\
--form 'number="6394856756"' \\
--form 'amount="10"' \\
--form 'pin="2242"' \\
--form 'circle="Delhi NCR"'`} />
    </div>
  );
}

function CreateDthRechargePage() {
  return (
    <div>
      <EndpointHeader
        title="Create DTH Recharge"
        desc="Initiate a DTH recharge (multipart/form-data)."
        method="POST" path="create_dth_recharge"
      />
      {HEADERS_NOTE}
      <SectionLabel style={{ marginTop: 0 }}>Form-data parameters</SectionLabel>
      <ParamTable rows={[
        { name: "operator", type: "string", required: true, desc: 'DTH operator name, e.g. "Tata Sky"' },
        { name: "number",   type: "string", required: true, desc: "DTH subscriber/customer ID" },
        { name: "amount",   type: "string", required: true, desc: "Recharge amount in INR" },
        { name: "pin",      type: "string", required: true, desc: "Transaction PIN" },
      ]} />
      <SectionLabel>curl</SectionLabel>
      <CodeBlock code={`curl --location 'https://pro.mokshiq.in/create_dth_recharge' \\
--header 'Authorization: {{Authorization}}' \\
--header 'Origin: {{Origin}}' \\
--form 'operator="Tata Sky"' \\
--form 'number="1234567890"' \\
--form 'amount="200"' \\
--form 'pin="2242"'`} />
    </div>
  );
}

function RechargeStatusPage() {
  return (
    <div>
      <EndpointHeader
        title="Recharge Status Check"
        desc="Check the current status of a submitted recharge transaction."
        method="GET" path="recharge_status_check" query="?id=rechargeid"
      />
      {HEADERS_NOTE}
      <SectionLabel style={{ marginTop: 0 }}>Query parameters</SectionLabel>
      <ParamTable rows={[{ name: "id", type: "string", required: true, desc: "The recharge report ID returned from the create call" }]} />
      <SectionLabel>curl</SectionLabel>
      <CodeBlock code={`curl --location 'https://pro.mokshiq.in/recharge_status_check?id=rechargeid' \\
--header 'Authorization: {{Authorization}}' \\
--header 'Origin: {{Origin}}'`} />
    </div>
  );
}

function RechargeCallbackPage() {
  return (
    <div>
      <EndpointHeader
        title="Recharge Callback"
        desc="Provider callback endpoint used to update recharge status asynchronously."
        method="GET" path="main_recharge_callback"
      />
      {HEADERS_NOTE}
      <SectionLabel style={{ marginTop: 0 }}>curl</SectionLabel>
      <CodeBlock code={`curl --location 'https://pro.mokshiq.in/main_recharge_callback' \\
--header 'Authorization: {{Authorization}}' \\
--header 'Origin: {{Origin}}'`} />
    </div>
  );
}

function BbpsOperatorPage() {
  return (
    <div>
      <EndpointHeader
        title="Fetch BBPS Operator"
        desc="Returns available BBPS billers."
        method="GET" path="fetch_bbps1_operator"
      />
      {HEADERS_NOTE}
      <SectionLabel style={{ marginTop: 0 }}>curl</SectionLabel>
      <CodeBlock code={`curl --location 'https://pro.mokshiq.in/fetch_bbps1_operator' \\
--header 'Authorization: {{Authorization}}' \\
--header 'Origin: {{Origin}}'`} />
    </div>
  );
}

function BbpsStatusPage() {
  return (
    <div>
      <EndpointHeader
        title="BBPS Status Check"
        desc="Check the status of a BBPS transaction."
        method="GET" path="bbps1_status_check" query="?txnid=<id>"
      />
      {HEADERS_NOTE}
      <SectionLabel style={{ marginTop: 0 }}>Query parameters</SectionLabel>
      <ParamTable rows={[{ name: "txnid", type: "string", required: true, desc: "BBPS transaction ID" }]} />
      <SectionLabel>curl</SectionLabel>
      <CodeBlock code={`curl --location 'https://pro.mokshiq.in/bbps1_status_check?txnid=<id>' \\
--header 'Authorization: {{Authorization}}' \\
--header 'Origin: {{Origin}}'`} />
    </div>
  );
}

function BbpsBillPage() {
  return (
    <div>
      <EndpointHeader
        title="Fetch Biller Info"
        desc="Fetch bill details for a customer before payment."
        method="POST" path="fetch_bbps1_bill"
      />
      {HEADERS_NOTE}
      <SectionLabel style={{ marginTop: 0 }}>Form-data parameters</SectionLabel>
      <ParamTable rows={[
        { name: "cn",   type: "string", required: true, desc: "Consumer number / account ID" },
        { name: "ad1",  type: "string", required: false, desc: "Additional data field 1 (biller-specific)" },
        { name: "ad2",  type: "string", required: false, desc: "Additional data field 2" },
        { name: "ad3",  type: "string", required: false, desc: "Additional data field 3" },
        { name: "ad4",  type: "string", required: false, desc: "Additional data field 4" },
        { name: "opid", type: "string", required: true, desc: "Operator / biller ID" },
      ]} />
      <SectionLabel>curl</SectionLabel>
      <CodeBlock code={`curl --location 'https://pro.mokshiq.in/fetch_bbps1_bill' \\
--header 'Authorization: {{Authorization}}' \\
--header 'Origin: {{Origin}}' \\
--form 'cn="<consumer_number>"' \\
--form 'ad1=""' \\
--form 'ad2=""' \\
--form 'ad3=""' \\
--form 'ad4=""' \\
--form 'opid="<operator_id>"'`} />
    </div>
  );
}

function BbpsPayPage({ manual }) {
  const path = manual ? "pay_bbps1_bill_mannual" : "pay_bbps1_bill";
  return (
    <div>
      <EndpointHeader
        title={manual ? "Pay BBPS Bill Offline" : "Pay BBPS Bill Online"}
        desc={manual
          ? "Record an offline/manual BBPS bill payment."
          : "Pay a fetched BBPS bill online."}
        method="POST" path={path}
      />
      {HEADERS_NOTE}
      <SectionLabel style={{ marginTop: 0 }}>Form-data parameters</SectionLabel>
      <ParamTable rows={[
        { name: "id",            type: "string", required: true,  desc: "Consumer number / bill ID" },
        { name: "ad1",           type: "string", required: false, desc: "Additional data field 1" },
        { name: "ad2",           type: "string", required: false, desc: "Additional data field 2" },
        { name: "ad3",           type: "string", required: false, desc: "Additional data field 3" },
        { name: "ad4",           type: "string", required: false, desc: "Additional data field 4" },
        { name: "opid",          type: "string", required: true,  desc: "Operator / biller ID" },
        { name: "amount",        type: "string", required: true,  desc: "Bill amount in INR" },
        { name: "customerName",  type: "string", required: true,  desc: "Customer name" },
        { name: "operatorName",  type: "string", required: true,  desc: "Biller / operator name" },
      ]} />
      <SectionLabel>curl</SectionLabel>
      <CodeBlock code={`curl --location 'https://pro.mokshiq.in/${path}' \\
--header 'Authorization: {{Authorization}}' \\
--header 'Origin: {{Origin}}' \\
--form 'id="<consumer_number>"' \\
--form 'ad1=""' \\
--form 'ad2=""' \\
--form 'ad3=""' \\
--form 'ad4=""' \\
--form 'opid="<operator_id>"' \\
--form 'amount="<amount>"' \\
--form 'customerName="<name>"' \\
--form 'operatorName="<operator>"'`} />
    </div>
  );
}

function BalancePage() {
  return (
    <div>
      <EndpointHeader
        title="Fetch API Balance"
        desc="Retrieve the authenticated account's wallet balance."
        method="GET" path="fetch_api_balance"
      />
      {HEADERS_NOTE}
      <SectionLabel style={{ marginTop: 0 }}>curl</SectionLabel>
      <CodeBlock code={`curl --location 'https://pro.mokshiq.in/fetch_api_balance' \\
--header 'Authorization: {{Authorization}}' \\
--header 'Origin: {{Origin}}'`} />
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────
const PAGE_MAP = {
  auth:              <AuthPage />,
  get_operator:      <GetOperatorPage />,
  dth_operator:      <DthOperatorPage />,
  get_circle:        <CircleePage />,
  create_mobile:     <CreateMobileRechargePage />,
  create_dth:        <CreateDthRechargePage />,
  recharge_status:   <RechargeStatusPage />,
  recharge_callback: <RechargeCallbackPage />,
  bbps_operator:     <BbpsOperatorPage />,
  bbps_status:       <BbpsStatusPage />,
  bbps_bill:         <BbpsBillPage />,
  bbps_pay:          <BbpsPayPage manual={false} />,
  bbps_pay_manual:   <BbpsPayPage manual={true} />,
  balance:           <BalancePage />,
};

export default function RechargeAPIDocs() {
  const [activePage, setActivePage] = useState("auth");

  return (
    <main>
      <div className="container-fluid">
        <div style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif", background: "#f1f5f9", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
          <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&family=DM+Mono:wght@400;500&display=swap" rel="stylesheet" />

          <div style={{
            background: "#fff", borderBottom: "0.5px solid #e2e8f0",
            padding: "12px 20px", display: "flex", alignItems: "center", gap: 16,
            position: "sticky", top: 0, zIndex: 100,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8, background: "#185FA5",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <span style={{ color: "#fff", fontSize: 14, fontWeight: 700 }}>M</span>
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>Mokshiq API</div>
                <div style={{ fontSize: 11, color: "#94a3b8" }}>pro.mokshiq.in · v1.0</div>
              </div>
            </div>
            <div style={{
              marginLeft: "auto", display: "flex", alignItems: "center", gap: 8,
              background: "#f8fafc", border: "0.5px solid #e2e8f0", borderRadius: 8,
              padding: "6px 12px", fontFamily: "monospace", fontSize: 11, color: "#64748b",
            }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#3B6D11", display: "inline-block" }} />
              https://pro.mokshiq.in
            </div>
          </div>

          <div style={{ display: "flex", flex: 1 }}>
            <div style={{
              width: 240, flexShrink: 0, background: "#fff", borderRight: "0.5px solid #e2e8f0",
              padding: "16px 0", position: "sticky", top: 57, height: "calc(100vh - 57px)", overflowY: "auto",
            }}>
              {NAV_ITEMS.map((item) => (
                <React.Fragment key={item.id}>
                  {item.section && (
                    <div style={{
                      padding: "12px 16px 4px", fontSize: 10, fontWeight: 600, letterSpacing: ".08em",
                      textTransform: "uppercase", color: "#94a3b8",
                    }}>
                      {item.section}
                    </div>
                  )}
                  <div
                    onClick={() => setActivePage(item.id)}
                    style={{
                      display: "flex", alignItems: "center", gap: 9, padding: "9px 16px", cursor: "pointer",
                      borderLeft: `2px solid ${activePage === item.id ? item.color : "transparent"}`,
                      background: activePage === item.id ? `${item.color}10` : "transparent",
                      color: activePage === item.id ? item.color : "#64748b",
                      fontWeight: activePage === item.id ? 600 : 400, fontSize: 13, transition: "all .15s",
                    }}
                  >
                    <span style={{
                      width: 7, height: 7, borderRadius: "50%", background: item.color, flexShrink: 0,
                      opacity: activePage === item.id ? 1 : 0.4,
                    }} />
                    {item.label}
                  </div>
                </React.Fragment>
              ))}
            </div>

            <div style={{ flex: 1, padding: "28px 32px", maxWidth: 860 }}>
              {PAGE_MAP[activePage]}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}